import React, { useState } from 'react';
import { KeyRound, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { changePassword } from '../lib/api';
import { toast } from 'sonner';

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setShow(false); };
  const close = () => { reset(); onOpenChange(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error("Passwords don't match"); return; }
    if (next === current) { toast.error('New password cannot match current'); return; }
    setBusy(true);
    try {
      await changePassword({ current_password: current, new_password: next });
      toast.success('Password updated');
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update password');
    } finally { setBusy(false); }
  };

  // Simple strength indicator: length + variety
  const strength = (() => {
    let s = 0; if (next.length >= 8) s++; if (next.length >= 12) s++;
    if (/[A-Z]/.test(next) && /[a-z]/.test(next)) s++;
    if (/\d/.test(next)) s++; if (/[^A-Za-z0-9]/.test(next)) s++;
    return Math.min(s, 4);
  })();
  const strengthLabel = ['Too short', 'Weak', 'Okay', 'Strong', 'Excellent'][strength];
  const strengthColor = ['#ff4b4b', '#ff8a4b', '#ffcf52', '#22d3ee', '#34d399'][strength];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[var(--theme-surface)] border-white/10 text-[var(--theme-fg)] max-w-[440px]" data-testid="change-password-dialog">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl grid place-items-center mb-2" style={{ background: 'linear-gradient(135deg,#a78bfa,#22d3ee)' }}>
            <KeyRound className="w-5 h-5 text-black" />
          </div>
          <DialogTitle className="font-display text-[22px]">Change your password</DialogTitle>
          <DialogDescription className="text-[var(--theme-muted)] text-[13px]">Use 8+ characters, ideally with numbers and symbols. You'll stay signed in.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <FieldPass label="Current password" value={current} onChange={setCurrent} show={show} testid="pwd-current" autoFocus />
          <FieldPass label="New password" value={next} onChange={setNext} show={show} testid="pwd-new" />
          {next && (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full transition-all duration-300" style={{ width: `${(strength / 4) * 100}%`, background: strengthColor }} />
              </div>
              <div className="text-[11px] font-semibold" style={{ color: strengthColor }}>{strengthLabel}</div>
            </div>
          )}
          <FieldPass label="Confirm new password" value={confirm} onChange={setConfirm} show={show} testid="pwd-confirm" />
          {confirm && next && next === confirm && (
            <div className="flex items-center gap-1.5 text-[#34d399] text-[12px]"><CheckCircle2 className="w-3.5 h-3.5" /> Passwords match</div>
          )}

          <label className="flex items-center gap-2 text-[12.5px] text-[var(--theme-muted)] cursor-pointer select-none pt-1">
            <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            Show passwords
          </label>

          <div className="flex items-center justify-end gap-2 pt-3">
            <button type="button" onClick={close} className="btn-ghost text-[12.5px]">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary text-[12.5px] disabled:opacity-60" data-testid="pwd-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Update password
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldPass({ label, value, onChange, show, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="block text-[11.5px] uppercase tracking-wider text-[var(--theme-muted)] mb-1 font-semibold">{label}</span>
      <input type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} data-testid={testid} autoFocus={autoFocus}
             className="input-field w-full" autoComplete="new-password" />
    </label>
  );
}
